import { LEAD_TYPE_IDS, REQUIRED_FEATURE_FLAGS, getProductTypeByName, PRODUCT_TYPE_IDS } from 'helpers/sf_constants';
import { useMemo } from 'react';
import useFeatureFlags from './useFeatureFlags';
import { filterLeadTypeOptionsforMotorCycleType } from 'helpers/sf_utils';

/**
 * Return the filtered lead types options.
 *
 * @param {Array} lead_types - Array of objects containing the lead types.
 * 
 * Example:
 * lead_types = [
 *   {
 *     id: 6,
 *     name: 'Auto',
 *   },
 *   {
 *     id: 9,
 *     name: 'Motocycle',
 *   },
 * ];
 * 
 * @param {Boolean} sendAsLabelValueFormat - Boolean value to check whether we need to send label value format.
 * 
 * Example:
 * sendAsLabelValueFormat = true;
 * 
 * If the flag is true:
 * lead_types = [
 *   {
 *     value: 6,
 *     label: 'Auto',
 *   },
 *   {
 *     value: 9,
 *     label: 'Motocycle',
 *   },
 * ];
 * 
 * If the flag is false:
 * lead_types = [
 *   {
 *     id: 6,
 *     name: 'Auto',
 *   },
 *   {
 *     id: 9,
 *     name: 'Motocycle',
 *   },
 * ];
 * 
 * @returns {Array} Filtered lead type options based on the feature flag.
 * 
 * If the feature flag is true:
 * lead_types = [
 *   {
 *     value: 6,
 *     label: 'Auto',
 *   },
 *   {
 *     value: 9,
 *     label: 'Motocycle',
 *   },
 * ];
 * 
 * If the feature flag is false:
 * lead_types = [
 *   {
 *     value: 6,
 *     label: 'Auto',
 *   },
 * ];
 */


const useFilteredLeadTypes = (lead_types, product_type, sendAsLabelValueFormat = true) => {
    const [isFeatureFlagActive] = useFeatureFlags();
    const product_type_id = getProductTypeByName(product_type)?.id
    const isAllProductTypeSelected = product_type === ''

    const motorCycleProfileFeatureFlag = useMemo(() => {
        return isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_MOTORCYCLE_LEAD_TYPE_FOR_CLICKS);
    }, [isFeatureFlagActive]);

    const ltGroupHealthEnabled = useMemo(() => {
        return isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ENABLE_GROUP_HEALTH_LEAD_TYPE);
    }, [isFeatureFlagActive]);

    const filteredLeadTypes = useMemo(() => {
        let filtered_lead_types = lead_types;

        // Filter out GROUP_HEALTH if feature flag is not enabled
        if (!ltGroupHealthEnabled) {
            filtered_lead_types = filtered_lead_types.filter((lt) => lt.id !== LEAD_TYPE_IDS.GROUP_HEALTH)
        }

        // Filter out P_C_BUNDLE based on product type
        // P_C_BUNDLE should only be available for CALLS product type
        if (product_type_id && product_type_id !== PRODUCT_TYPE_IDS.CALLS) {
            filtered_lead_types = filtered_lead_types.filter((lt) => lt.id !== LEAD_TYPE_IDS.P_C_BUNDLE);
        }

        // Apply motorcycle filtering using existing function
        return filterLeadTypeOptionsforMotorCycleType(filtered_lead_types, motorCycleProfileFeatureFlag, product_type_id, sendAsLabelValueFormat, isAllProductTypeSelected);
    }, [lead_types, motorCycleProfileFeatureFlag, product_type_id, ltGroupHealthEnabled, sendAsLabelValueFormat, isAllProductTypeSelected]);

    return filteredLeadTypes;
};

export default useFilteredLeadTypes;
